import { cn } from '@kalos/ui';
import { CheckIcon } from '@radix-ui/react-icons';

import { useSchedulerStyle } from '../context/schedulerStyle';

export type JobCreationStep = 'search' | 'review' | 'schedule';

export interface StepIndicatorProps {
  currentStep: JobCreationStep;
  /**
   * Invoked when a previously-completed step is clicked, to navigate back to it.
   * Only completed steps (before the current one) are clickable; omitting this
   * makes the indicator purely visual.
   */
  onStepClick?: (step: JobCreationStep) => void;
}

interface StepConfig {
  id: JobCreationStep;
  label: string;
  number: number;
}

const STEPS: StepConfig[] = [
  { id: 'search', label: 'Search', number: 1 },
  { id: 'review', label: 'Review', number: 2 },
  { id: 'schedule', label: 'Schedule', number: 3 },
];

const getStepIndex = (step: JobCreationStep): number => {
  return STEPS.findIndex((s) => s.id === step);
};

export const StepIndicator = ({ currentStep, onStepClick }: StepIndicatorProps) => {
  const currentIndex = getStepIndex(currentStep);
  const compact = useSchedulerStyle();
  // Compact label styling applies to Search/Review only; the Schedule step
  // reverts to the original look (matches JobCreationFlow's chrome).
  const compactLabels = compact && currentStep !== 'schedule';

  return (
    <div className="flex items-center justify-center gap-2">
      {STEPS.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        const isPending = index > currentIndex;
        // Only completed steps can be clicked, and only to navigate backward.
        const isClickable = isCompleted && !!onStepClick;

        return (
          <div key={step.id} className="flex items-center gap-2">
            {/* Step indicator */}
            <button
              type="button"
              disabled={!isClickable}
              onClick={isClickable ? () => onStepClick?.(step.id) : undefined}
              className={cn(
                'flex items-center gap-2',
                isClickable
                  ? 'focus-visible:ring-primary cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2'
                  : 'cursor-default',
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                  isCompleted && 'bg-primary text-white',
                  isCurrent && 'bg-primary text-white',
                  isPending && 'bg-gray-200 text-gray-600',
                )}
              >
                {isCompleted ? <CheckIcon className="h-4 w-4" /> : <span>{step.number}</span>}
              </div>
              <span
                className={cn(
                  compactLabels ? 'text-xs font-semibold' : 'text-sm font-medium',
                  isCompleted && 'text-gray-900',
                  isCurrent && 'text-gray-900',
                  isPending && 'text-gray-500',
                  isClickable && 'underline-offset-4 hover:underline',
                )}
              >
                {step.label}
              </span>
            </button>

            {/* Connector line (except for last step) */}
            {index < STEPS.length - 1 && (
              <div
                // Short on phones so all three steps fit a 360px screen; desktop keeps w-12.
                className={cn(
                  'h-0.5 w-4 transition-colors sm:w-12',
                  index < currentIndex ? 'bg-primary' : 'bg-gray-200',
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
